import { computed, ref } from 'vue';
import { getReasonMessage } from '@/shared/common';
import { useWalletStore } from '@/stores/wallet';
import { useAdditionalAccountStore } from '../stores/additionalAccount.store';
import { additionalAccountService } from '../services/additionalAccount.service';
import { getCompanyAccountsCatalogAdapter } from '../adapters/companyAccountsCatalog.adapter';
import {
  ErrorsAdditionalAccount,
  COMPANY_ACCOUNTS_CATALOG_ERROR_MESSAGE,
} from '../errors/additionalAccount.error';
import type { WalletType } from '@/types/WalletType';
import type { CompanyAccountCurrencyGroup } from '../types/companyAccountsSelect.type';
import type {
  CompanyAccountCatalogEntry,
  LoadCompanyAccountsCatalogOptions,
  ToCatalogEntryParams,
  UseCompanyAccountsCatalogReturn,
} from '../types/companyAccountsCatalog.type';

/**
 * Cruza una cuenta del catálogo con su wallet operativa. `walletsById` se arma
 * una sola vez por carga (O(1) el lookup acá adentro, no un `.find()` por
 * cuenta). Sin wallet asociada la cuenta queda con sus campos operativos en
 * `null` — nunca hereda un saldo o un `checkDataAccount` de otra cuenta ni cae
 * a `0`, que es indistinguible de un saldo real.
 *
 * `isOperable` depende solo de que la wallet traiga `checkDataAccount`: el backend
 * ya filtra el catálogo y solo devuelve las cuentas del usuario. La wallet sola
 * no alcanza: toda cuenta adicional tiene una —la sintética del adapter del
 * catálogo—, así que sin mirar el blob el 500 de `quote/b2b` aparecería recién
 * al cotizar.
 */
function toCatalogEntry({
  account,
  currency,
  fallbackFlagCountryCode,
  walletsById,
}: ToCatalogEntryParams): CompanyAccountCatalogEntry {
  const wallet = walletsById.get(account.id);

  return {
    id: account.id,
    name: account.name,
    accountNumber: account.accountNumber,
    isMain: account.isMain,
    currency,
    flagCountryCode: wallet?.flagCountryCode ?? fallbackFlagCountryCode,
    isOperable: Boolean(wallet?.checkDataAccount),
    balance: wallet?.balance ?? null,
    checkDataAccount: wallet?.checkDataAccount ?? null,
    accountBranchName: wallet?.accountBranchName ?? null,
  };
}

/**
 * Catálogo de cuentas de la empresa para el cotizador "Entre cuentas".
 * `grouped-by-currency` es la verdad de qué cuentas existen y cuáles son
 * adicionales, pero no trae nada operativo: el saldo, el `checkDataAccount`, el
 * `flagCountryCode` y el `accountBranchName` salen de cruzar por
 * `id`/`walletId` contra las wallets.
 *
 * Y las wallets son DOS fuentes, no una. `/wallets` devuelve solo las
 * principales, una por moneda; las adicionales las aporta el store
 * (`additionalAccountStore.accounts`), que las construye desde
 * `grouped-by-currency`: `loadCatalog` le pasa la respuesta que ya pidió, así
 * cada carga hace un solo GET y sincroniza los saldos del store. Cruzar contra `/wallets` a secas dejaría
 * a toda cuenta adicional sin saldo y fuera del listado.
 *
 * El backend ya filtra el catálogo: solo trae las cuentas que el usuario opera, y
 * `isOperable` es `true` cuando además la wallet trae `checkDataAccount`. Si el
 * catálogo falla no hay cuentas: nada es operable. Esa bandera restringe el ORIGEN (vía `filterOperableOriginAccounts`) tanto en
 * los cotizadores que usan `useQuoteOriginAccounts` como en entre cuentas; el
 * DESTINO de entre cuentas no se filtra por operabilidad. `hasLoaded` +
 * `errorMessage` distinguen "no cargó todavía" de "cargó y no es operable".
 */
export function useCompanyAccountsCatalog(): UseCompanyAccountsCatalogReturn {
  const walletStore = useWalletStore();
  const additionalAccountStore = useAdditionalAccountStore();

  const catalogGroups = ref<CompanyAccountCurrencyGroup[]>([]);
  const isLoading = ref(false);
  const errorMessage = ref('');
  const hasLoaded = ref(false);

  /**
   * Índice de wallets por `walletId` sobre las dos fuentes. Es la misma base del cruce y del
   * resolver que se expone, así que se arma una vez y no dos.
   */
  const walletsById = computed<Map<number, WalletType>>(() => {
    const additionalWallets = additionalAccountStore.accounts.map((account) => account.wallet);

    return new Map<number, WalletType>(
      [...walletStore.wallets, ...additionalWallets].map(
        (wallet): [number, WalletType] => [wallet.walletId, wallet]
      )
    );
  });

  const accounts = computed<CompanyAccountCatalogEntry[]>(() =>
    catalogGroups.value.flatMap((group) =>
      group.accounts.map((account) =>
        toCatalogEntry({
          account,
          currency: group.currency,
          fallbackFlagCountryCode: group.flagCountryCode,
          walletsById: walletsById.value,
        })
      )
    )
  );

  function findWalletByAccountId(accountId: string): WalletType | undefined {
    return walletsById.value.get(Number(accountId));
  }

  /**
   * Un solo GET. La promesa se le pasa al store sin esperarla, así el store prende su loading y
   * pide `/wallets` mientras el GET sigue en vuelo; `isLoading` baja cuando terminan los dos.
   * Por defecto el store se sincroniza sin tocar su loading/error global; solo
   * `accountsUpdate: 'refresh'` lo recarga con flags. Cualquier otro primer argumento (un evento
   * de `@retry`) cae en sync.
   */
  async function loadCatalog(options?: LoadCompanyAccountsCatalogOptions): Promise<void> {
    isLoading.value = true;
    errorMessage.value = '';

    const resultPromise = additionalAccountService.getAccountsGroupedByCurrency();
    const storeUpdate =
      options?.accountsUpdate === 'refresh'
        ? additionalAccountStore.refreshAdditionalAccounts(resultPromise)
        : additionalAccountStore.syncAdditionalAccounts(resultPromise);

    const [result] = await Promise.all([resultPromise, storeUpdate]);

    result.fold(
      (failure) => {
        errorMessage.value = getReasonMessage({
          failure,
          errorMap: ErrorsAdditionalAccount,
          defaultMessage: COMPANY_ACCOUNTS_CATALOG_ERROR_MESSAGE,
        });
        catalogGroups.value = [];
      },
      (success) => {
        catalogGroups.value = getCompanyAccountsCatalogAdapter(success.data);
      }
    );

    hasLoaded.value = true;
    isLoading.value = false;
  }

  return {
    accounts,
    isLoading,
    errorMessage,
    hasLoaded,
    loadCatalog,
    findWalletByAccountId,
  };
}
